/**
 * Number formatting helper utilities
 */

/**
 * Format number with thousand separators (commas)
 * @param value - The number to format
 * @returns Formatted string with commas (e.g., 1,000,000)
 * @example
 * formatNumberWithCommas(1000000) // Returns "1,000,000"
 * formatNumberWithCommas(null) // Returns ""
 */
export function formatNumberWithCommas(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Parse formatted string back to number by removing commas
 * @param value - The formatted string to parse
 * @returns Numeric value without separators
 * @example
 * parseFormattedNumber("1,000,000") // Returns 1000000
 * parseFormattedNumber("") // Returns 0
 */
export function parseFormattedNumber(value: string): number {
  if (!value) return 0;
  // Remove all commas (thousand separators) and parse
  const cleaned = value.replace(/,/g, '');
  const parsed = parseInt(cleaned, 10);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Format number with thousand separators (periods)
 * @param value - The number to format
 * @returns Formatted string with periods (e.g., 1.000.000)
 * @example
 * formatNumberWithPeriods(1000000) // Returns "1.000.000"
 */
export function formatNumberWithPeriods(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Parse formatted string with periods back to number
 * @param value - The formatted string to parse
 * @returns Numeric value without separators
 * @example
 * parseFormattedNumberWithPeriods("1.000.000") // Returns 1000000
 */
export function parseFormattedNumberWithPeriods(value: string): number {
  if (!value) return 0;
  // Remove all periods (thousand separators) and parse
  const cleaned = value.replace(/\./g, '');
  const parsed = parseInt(cleaned, 10);
  return isNaN(parsed) ? 0 : parsed;
}
